import dayjs from 'dayjs';

import { MAX_ATTEMPTS, TILE_COUNT } from '../constants';
import { Guess } from '../hooks/useGuessHistory';
import { getPuzzleNumber } from './helpers';

/**
 * Shared shape of a finished daily game, used by both the share text and the
 * end screen so the two can never drift apart.
 */
export type DailyResult = {
  /** Attempts used, or 'DNF' when the flag was never found. */
  score: number | 'DNF';
  guesses: Guess[];
  dayString: string;
};

/** The 3x2 block of green/red squares, as it appears in a shared message. */
export const generateShareSquares = ({
  score,
  guesses,
}: {
  score: number | 'DNF';
  guesses: Guess[];
}) => {
  if (score === 'DNF') {
    return '🟥🟥🟥\n🟥🟥🟥\n';
  }

  const squares = Array(TILE_COUNT).fill('🟩');
  for (let i = 0; i < guesses.length - 1; i++) {
    squares[guesses[i].tile] = '🟥';
  }

  for (let i = 0; i < TILE_COUNT; i++) {
    if ((i + 1) % 3 === 0) {
      squares[i] += '\n';
    }
  }
  return squares.join('');
};

/**
 * The same squares as a flat array, for rendering them as tiles on screen.
 *
 * Array.from, not split(''): these emoji are surrogate pairs, and splitting by
 * UTF-16 code unit would hand back two broken halves per square.
 */
export const shareSquaresAsTiles = (result: DailyResult): string[] =>
  Array.from(generateShareSquares(result).replace(/\n/g, ''));

/** `#Flagle #1641 (20.08.2025) 4/6` — the first line of a shared message. */
export const buildResultHeadline = ({ score, dayString }: DailyResult) =>
  `#Flagle #${getPuzzleNumber(dayString)} (${dayjs(dayString).format(
    'DD.MM.YYYY',
  )}) ${score === 'DNF' ? 'X' : score}/${MAX_ATTEMPTS}`;

/**
 * The full message body: headline, squares, then the bonus-round emojis.
 * Both the share button and the end screen's share icons build from this, so
 * a player gets the same text whichever control they use.
 */
export const buildShareBody = (result: DailyResult, roundEmojis: string) =>
  `${buildResultHeadline(result)}\n${generateShareSquares(result)}${roundEmojis}`;
